import { ReactElement, useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { FeeProfile, feeProfilesCreateDeleteCr, feeProfilesValidateDelete } from '@pmp/api';
import { usePmpFetch } from '@pmp/hooks';
import { CrudMode } from '@ohpen/enso-ui';

import { FeeProfilesTabProps } from '../interfaces';
import { getInitialState, reducer } from '../reducer';
import { FeeProfilesList } from './FeeProfilesList';
import { FeeProfilesCrudForm } from './FeeProfilesCrudForm';
import { ConfirmCrDeleteModal } from '@pmp/components';

export const FeeProfilesTab = ({ valueLists }: FeeProfilesTabProps): ReactElement => {
  const [isFetching, , data, , fetchData] = usePmpFetch('feeProfilesGetAllFeeProfiles');
  const [state, dispatch] = useReducer(reducer<FeeProfile>, getInitialState<FeeProfile>());
  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: FeeProfile) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEdit = useCallback((record: FeeProfile) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: FeeProfile) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  return (
    <>
      <FeeProfilesList
        data={data ?? []}
        isFetching={isFetching}
        valueLists={valueLists}
        onCreate={onCreate}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      {state.crud.open && state.crud.mode && (
        <FeeProfilesCrudForm
          mode={state.crud.mode}
          record={state.crud.record}
          valueLists={valueLists}
          refreshTable={fetchData}
          onClose={onCloseCrud}
        />
      )}
      <ConfirmCrDeleteModal
        open={state.deleteModal.open}
        records={state.deleteModal.record ? [state.deleteModal.record] : []}
        idKey="feeProfileId"
        nameKey="feeProfileName"
        entityLabel={t('pmp.fees.labels.feeProfile')}
        entityLabelPlural={t('pmp.fees.labels.feeProfiles')}
        getPrecheck={feeProfilesValidateDelete}
        createDeleteChangeRequest={feeProfilesCreateDeleteCr}
        refreshTable={fetchData}
        onClose={onCloseDeleteModal}
      />
    </>
  );
};
